/* ==========================================================================
   Kankatala PDP — sections/kankatala-main-product.liquid
   - Smart sticky columns (desktop): the shorter column pins once its bottom is
     on screen, the taller one keeps scrolling.
   - Accordions and the Share popover (close on outside click / Esc, copy link).
   - Zoom: Prestige's own PhotoSwipe lightbox (product-gallery.lightBox), with a
     zoom button on every image, tap-to-zoom on mobile, a counter, thumbnails,
     a hint, 2400px images and mouse panning added through PhotoSwipe's API.
   - Mobile sticky bar and the sold-out "Similar, in stock" block.
   - Ticked tailoring add-ons are added into every shown price (main + sticky bar).
   Idempotent: every root is initialised once (data-kk-pdp-init) and again on
   shopify:section:load.
   ========================================================================== */
(function () {
  'use strict';

  // Prestige's "md" breakpoint: gallery grid vs slider, and Prestige opens the lightbox on click from here up
  var DESKTOP = '(min-width: 1000px)';
  var HINT_KEY = 'kkPdpZoomed';
  var hookedLightboxes = new WeakSet();

  function isDesktop() { return window.matchMedia(DESKTOP).matches; }

  function storageGet(key) { try { return window.sessionStorage.getItem(key); } catch (e) { return null; } }
  function storageSet(key, value) { try { window.sessionStorage.setItem(key, value); } catch (e) {} }

  /* Height of Prestige's sticky header area (--sticky-area-height is a calc(), so measure it) */
  function stickyAreaHeight() {
    var probe = document.createElement('div');
    probe.style.cssText = 'position:absolute;visibility:hidden;pointer-events:none;height:var(--sticky-area-height, 0px)';
    document.body.appendChild(probe);
    var height = probe.offsetHeight;
    probe.remove();
    return height;
  }

  /* ------------------------------------------------------------------------
     Smart sticky
  ------------------------------------------------------------------------ */
  function updatePdpSticky(root) {
    var cols = root.querySelectorAll('[data-pdp-sticky]');
    if (!isDesktop()) {
      cols.forEach(function (col) { col.style.top = ''; });
      return;
    }
    var header = stickyAreaHeight();
    var gap = 16;
    var vh = window.innerHeight;
    cols.forEach(function (col) {
      var h = col.offsetHeight;
      col.style.top = (h + header + gap <= vh) ? (header + gap) + 'px' : (vh - h - gap) + 'px';
    });
  }

  /* ------------------------------------------------------------------------
     Accordions (several open at once)
  ------------------------------------------------------------------------ */
  function onAccordionClick(event) {
    var toggle = event.target.closest('[data-kk-pdp-accordion]');
    if (!toggle) return;
    var panel = document.getElementById(toggle.getAttribute('aria-controls'));
    var open = toggle.getAttribute('aria-expanded') !== 'true';
    toggle.setAttribute('aria-expanded', String(open));
    if (panel) panel.hidden = !open;
  }

  /* ------------------------------------------------------------------------
     Share popover (snippets/kankatala-pdp-share.liquid)
  ------------------------------------------------------------------------ */
  function closeShares(root, except) {
    root.querySelectorAll('[data-kk-pdp-share][open]').forEach(function (share) {
      if (share !== except) share.open = false;
    });
  }

  function copyLink(button) {
    var url = button.getAttribute('data-kk-copy');
    var done = function () {
      button.classList.add('is-copied');
      button.setAttribute('aria-label', 'Link copied');
      window.setTimeout(function () {
        button.classList.remove('is-copied');
        button.setAttribute('aria-label', 'Copy link');
      }, 2000);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(done, function () { window.prompt('Copy link', url); });
    } else {
      window.prompt('Copy link', url);
    }
  }

  function onShareClick(root, event) {
    var copy = event.target.closest('.kk-pdp-share [data-kk-copy]');
    if (copy) {
      event.preventDefault();
      copyLink(copy);
      return;
    }
    closeShares(root, event.target.closest('[data-kk-pdp-share]'));
  }

  /* ------------------------------------------------------------------------
     Zoom
  ------------------------------------------------------------------------ */
  var ZOOM_ICON = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1c1c1c" stroke-width="1.6" stroke-linecap="round" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5"/><path d="m15.5 15.5 5 5M10.5 7.5v6M7.5 10.5h6"/></svg>';

  function imageCells(gallery) {
    return Array.prototype.slice.call(gallery.querySelectorAll('.product-gallery__media[data-media-type="image"]'))
      .filter(function (cell) { return !cell.hidden; });
  }

  function addZoomButtons(root) {
    var gallery = root.querySelector('product-gallery');
    if (!gallery || !gallery.hasAttribute('allow-zoom')) return;
    gallery.querySelectorAll('.product-gallery__media[data-media-type="image"]').forEach(function (cell) {
      if (cell.querySelector('.kk-pdp-zoom')) return;
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'kk-pdp-zoom';
      button.setAttribute('aria-label', 'Zoom image');
      button.innerHTML = ZOOM_ICON;
      cell.appendChild(button);
    });
  }

  function openZoom(root, cell, trigger) {
    var gallery = root.querySelector('product-gallery');
    var index = imageCells(gallery).indexOf(cell);
    if (index < 0) return;
    root.kkZoomTrigger = trigger || cell.querySelector('.kk-pdp-zoom');
    gallery.dispatchEvent(new CustomEvent('lightbox:open', { bubbles: true, detail: { index: index } }));
  }

  function onGalleryClick(root, event) {
    var button = event.target.closest('.kk-pdp-zoom');
    if (button) {
      event.preventDefault();
      event.stopPropagation(); // Prestige's carousel click handler would open it a second time
      openZoom(root, button.closest('.product-gallery__media'), button);
      return;
    }
    if (event.target.tagName !== 'IMG') return;
    var cell = event.target.closest('.product-gallery__media[data-media-type="image"]');
    if (!cell) return;
    // Desktop: Prestige opens the viewer on image click itself; just remember where to return focus.
    // Below 1000px Prestige only zooms on pinch: a tap on the image opens the viewer here.
    // A swipe never fires "click", so swiping the slider keeps changing images.
    if (isDesktop()) {
      root.kkZoomTrigger = cell.querySelector('.kk-pdp-zoom');
      return;
    }
    openZoom(root, cell);
  }

  /* 2400px source inside the viewer, page images stay at their normal size */
  function upgradeItemData(data) {
    if (!data || !data.src) return data;
    try {
      var url = new URL(data.src, window.location.href);
      url.searchParams.set('width', '2400');
      data.src = url.toString();
      data.srcset = '';
      if (data.width > 2400) {
        data.height = Math.round(data.height * 2400 / data.width);
        data.width = 2400;
      }
    } catch (e) {}
    return data;
  }

  function isZoomed(pswp) {
    var slide = pswp.currSlide;
    return !!slide && slide.currZoomLevel > slide.zoomLevels.initial * 1.01;
  }

  function registerZoomUi(root, lightbox) {
    var pswp = lightbox.pswp;
    var count = pswp.getNumItems();
    var fine = window.matchMedia('(pointer: fine)').matches;
    pswp.element.classList.add('kk-pswp');

    // Prestige's close / previous / next buttons only carry its utility classes: tag them for kankatala-pdp.css
    pswp.on('afterInit', function () {
      var buttons = Array.prototype.slice.call(pswp.element.querySelectorAll('.pswp__top-bar > .pswp__button'));
      var arrows = buttons.filter(function (button) { return !button.classList.contains('circle-button--xl'); });
      buttons.forEach(function (button) {
        if (button.classList.contains('circle-button--xl')) button.classList.add('kk-pswp-close');
      });
      if (arrows.length === 2) {
        arrows[0].classList.add('kk-pswp-prev');
        arrows[1].classList.add('kk-pswp-next');
      }
    });

    pswp.ui.registerElement({
      name: 'kk-counter',
      appendTo: 'root',
      onInit: function (el) {
        el.setAttribute('aria-live', 'polite');
        var update = function () { el.textContent = (pswp.currIndex + 1) + ' / ' + count; };
        pswp.on('change', update);
        update();
      }
    });

    if (count > 1) {
      pswp.ui.registerElement({
        name: 'kk-thumbs',
        appendTo: 'root',
        onInit: function (el) {
          var source = pswp.options.dataSource || [];
          var buttons = [];
          for (var i = 0; i < count; i++) {
            var item = source[i] || {};
            var button = document.createElement('button');
            button.type = 'button';
            button.className = 'kk-pswp-thumb';
            button.setAttribute('aria-label', 'Image ' + (i + 1));
            if (item.msrc) {
              var img = document.createElement('img');
              img.src = item.msrc;
              img.alt = '';
              button.appendChild(img);
            }
            button.addEventListener('click', pswp.goTo.bind(pswp, i));
            el.appendChild(button);
            buttons.push(button);
          }
          var update = function () {
            buttons.forEach(function (button, index) {
              button.setAttribute('aria-current', String(index === pswp.currIndex));
            });
            var current = buttons[pswp.currIndex];
            if (current) current.scrollIntoView({ block: 'nearest', inline: 'nearest' });
          };
          pswp.on('change', update);
          update();
        }
      });
    }

    pswp.ui.registerElement({
      name: 'kk-hint',
      appendTo: 'root',
      onInit: function (el) {
        el.textContent = fine
          ? 'Click to zoom in · move the mouse to look around · Esc to close · ← → for next'
          : 'Double-tap or pinch to zoom · swipe for next';
        var update = function () {
          var zoomed = isZoomed(pswp);
          el.classList.toggle('is-hidden', zoomed);
          if (zoomed) storageSet(HINT_KEY, '1');
        };
        pswp.on('zoomPanUpdate', update);
        pswp.on('change', update);
      }
    });

    // Desktop: once zoomed in, moving the mouse pans the image
    if (fine) {
      pswp.element.addEventListener('pointermove', function (event) {
        if (event.pointerType !== 'mouse' || !isZoomed(pswp)) return;
        var slide = pswp.currSlide;
        var bounds = slide.bounds;
        var rx = Math.min(Math.max(event.clientX / window.innerWidth, 0), 1);
        var ry = Math.min(Math.max(event.clientY / window.innerHeight, 0), 1);
        slide.pan.x = bounds.max.x - (bounds.max.x - bounds.min.x) * rx;
        slide.pan.y = bounds.max.y - (bounds.max.y - bounds.min.y) * ry;
        slide.applyCurrentZoomPan();
      });
    }
  }

  function hookLightbox(root, gallery) {
    var lightbox = gallery.lightBox; // Prestige getter: creates the PhotoSwipe instance on demand
    if (!lightbox || hookedLightboxes.has(lightbox)) return;
    hookedLightboxes.add(lightbox);

    Object.assign(lightbox.options, {
      imageClickAction: 'zoom', // click toggles zoom in / out (never closes)
      bgClickAction: 'close',
      tapAction: false,
      doubleTapAction: 'zoom',
      closeOnVerticalDrag: true,
      returnFocus: false,
      // Room for the counter row, the thumbnails (left column on desktop, bottom strip on mobile) and the hint
      paddingFn: function () {
        return isDesktop()
          ? { top: 56, bottom: 40, left: 100, right: 24 }
          : { top: 56, bottom: 76, left: 0, right: 0 };
      },
      // Levels are relative to the 2400px source: click zooms to 2.5x the fitted size
      secondaryZoomLevel: function (levels) { return levels.fit * 2.5; },
      maxZoomLevel: function (levels) { return Math.max(levels.fit * 4, 1); }
    });

    lightbox.addFilter('itemData', upgradeItemData);
    lightbox.on('uiRegister', function () { registerZoomUi(root, lightbox); });
    lightbox.on('beforeOpen', function () {
      document.documentElement.classList.add('kk-pdp-zoom-open');
      storageSet(HINT_KEY, '1');
      var hint = root.querySelector('[data-kk-pdp-tap-hint]');
      if (hint) hint.hidden = true;
    });
    lightbox.on('destroy', function () {
      document.documentElement.classList.remove('kk-pdp-zoom-open');
      var trigger = root.kkZoomTrigger;
      root.kkZoomTrigger = null;
      if (trigger && document.contains(trigger)) trigger.focus({ preventScroll: true });
    });
  }

  /* ------------------------------------------------------------------------
     Mobile sticky bar
  ------------------------------------------------------------------------ */
  function productForm(root) {
    return document.forms[root.getAttribute('data-form-id')];
  }

  function onBarClick(root, event) {
    if (event.target.closest('[data-kk-pdp-bar-atc]')) {
      var form = productForm(root);
      if (!form) return;
      var submit = form.querySelector('[type="submit"]');
      if (form.requestSubmit) form.requestSubmit(submit || undefined);
      else if (submit) submit.click();
      return;
    }
    if (event.target.closest('[data-kk-pdp-bar-buy]')) {
      var payment = root.querySelector('[data-kk-pdp-main-buttons] .shopify-payment-button__button');
      if (payment) payment.click();
      return;
    }
    if (event.target.closest('[data-kk-pdp-scroll-similar]')) {
      var target = root.querySelector('[data-kk-pdp-similar]:not([hidden])') || document.querySelector('[id$="__related-products"]');
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  function observeMainButtons(root) {
    var bar = root.querySelector('[data-kk-pdp-bar]');
    var buttons = root.querySelector('[data-kk-pdp-main-buttons]');
    if (root.kkBarObserver) root.kkBarObserver.disconnect();
    if (!bar || !buttons || buttons.hidden) return;

    // The area under the sticky header counts as "out of view"
    var header = stickyAreaHeight();
    root.kkBarObserver = new IntersectionObserver(function (entries) {
      var entry = entries[0];
      // Only once the buttons have scrolled away above the viewport (not while they are still below it)
      var past = !entry.isIntersecting && entry.boundingClientRect.bottom <= header;
      bar.classList.toggle('is-visible', past);
    }, { rootMargin: (-header) + 'px 0px 0px 0px' });
    root.kkBarObserver.observe(buttons);

    var payment = buttons.querySelector('.shopify-payment-button');
    var barBuy = bar.querySelector('[data-kk-pdp-bar-buy]');
    if (barBuy && !payment) barBuy.hidden = true;
  }

  function updateBarSpace(root) {
    var bar = root.querySelector('[data-kk-pdp-bar]');
    if (!bar) return;
    document.documentElement.style.setProperty('--kk-pdp-bar-height', (isDesktop() ? 0 : bar.offsetHeight) + 'px');
    document.documentElement.classList.add('kk-pdp-has-bar');
  }

  /* ------------------------------------------------------------------------
     Sold out: Shopify recommendations, collection fallback already rendered
  ------------------------------------------------------------------------ */
  function loadSimilar(root) {
    var block = root.querySelector('[data-kk-pdp-similar]');
    if (!block || block.dataset.kkLoaded) return;
    block.dataset.kkLoaded = 'true';
    fetch(block.getAttribute('data-url'))
      .then(function (response) { return response.ok ? response.text() : ''; })
      .then(function (html) {
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var results = doc.querySelector('[data-kk-pdp-similar-results]');
        if (!results || !results.querySelector('li')) return;
        block.querySelector('[data-kk-pdp-similar-body]').innerHTML = results.innerHTML;
        block.hidden = false;
      })
      .catch(function () {});
  }

  /* ------------------------------------------------------------------------
     Add-on total: [data-kk-price] rows (snippets/kankatala-price.liquid, live: true) carry the
     saree price / original price in cents; shown amounts = those + ticked add-ons. "% off" and
     "You save" are the saree's own and are left alone. Money: the shop's money format when the
     visitor shops in the shop currency (same output as Liquid's money_without_trailing_zeros),
     else Intl with the active currency.
  ------------------------------------------------------------------------ */
  function formatMoney(root, cents) {
    var data = root.dataset;
    if (data.currency && data.currency !== data.shopCurrency) {
      try {
        return new Intl.NumberFormat(data.locale || undefined, {
          style: 'currency', currency: data.currency, minimumFractionDigits: cents % 100 ? 2 : 0
        }).format(cents / 100);
      } catch (e) { /* fall through to the shop format */ }
    }

    var format = data.moneyFormat || '{{amount}}';
    var match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    var type = match ? match[1] : 'amount';
    var precision = cents % 100 ? 2 : 0; // "without trailing zeros"
    var group = function (value, digits, thousands, decimal) {
      var parts = (value / 100).toFixed(digits).split('.');
      return parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands) + (parts[1] ? decimal + parts[1] : '');
    };
    var amount;
    switch (type) {
      case 'amount_no_decimals': amount = group(cents, 0, ',', '.'); break;
      case 'amount_with_comma_separator': amount = group(cents, precision, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': amount = group(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': amount = group(cents, precision, "'", '.'); break;
      case 'amount_with_space_separator': amount = group(cents, precision, ' ', ','); break;
      default: amount = group(cents, precision, ',', '.');
    }
    return format.replace(/\{\{\s*\w+\s*\}\}/, amount);
  }

  function addonCents(root) {
    var total = 0;
    root.querySelectorAll('[data-kankatala-tailoring] .kankatala-tailoring__input:checked:not(:disabled)').forEach(function (input) {
      total += parseInt(input.dataset.price, 10) || 0;
    });
    return total;
  }

  function setText(el, text) {
    if (el && el.textContent !== text) el.textContent = text; // no-op when unchanged (keeps the MutationObserver quiet)
  }

  /* The shop money format is `<span class=money>...</span>`, which is one of
     the price selectors BUCKS converts. Writing textContent straight onto
     [data-kk-price-sale] would delete that span and leave the add-on price
     stuck in rupees, so write INSIDE the .money span instead, recreating it
     if it is missing. BUCKS is then asked to re-convert. */
  function setMoney(el, text) {
    if (!el) return;
    var target = el.querySelector('.money');
    if (!target) {
      target = document.createElement('span');
      target.className = 'money';
      el.textContent = '';
      el.appendChild(target);
    }
    if (target.textContent !== text) target.textContent = text;
    if (typeof window.kankatalaBucksRerender === 'function') window.kankatalaBucksRerender();
  }

  function updateAddonPrices(root) {
    var extra = addonCents(root);
    root.querySelectorAll('[data-kk-price]').forEach(function (row) {
      var price = parseInt(row.dataset.price, 10);
      var compare = parseInt(row.dataset.compare, 10);
      if (!isNaN(price)) setMoney(row.querySelector('[data-kk-price-sale]'), formatMoney(root, price + extra));
      if (!isNaN(compare)) setMoney(row.querySelector('[data-kk-price-compare]'), formatMoney(root, compare + extra));
    });
  }

  /* ------------------------------------------------------------------------
     Refresh after Prestige re-renders parts of the section (variant change)
  ------------------------------------------------------------------------ */
  function refresh(root) {
    addZoomButtons(root);
    updateAddonPrices(root); // a variant change re-renders the price at the bare saree price
    observeMainButtons(root);
    updateBarSpace(root);
    updatePdpSticky(root);
  }

  function init(root) {
    if (root.dataset.kkPdpInit) return;
    root.dataset.kkPdpInit = 'true';

    root.addEventListener('click', onAccordionClick);
    root.addEventListener('change', function (event) {
      if (event.target.closest('[data-kankatala-tailoring]')) updateAddonPrices(root);
    });
    root.addEventListener('click', function (event) { onShareClick(root, event); });
    root.kkOnDocClick = function (event) { if (!root.contains(event.target)) closeShares(root); };
    document.addEventListener('click', root.kkOnDocClick);
    root.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      var share = event.target.closest('[data-kk-pdp-share][open]');
      if (!share) return;
      share.open = false;
      share.querySelector('summary').focus();
    });
    root.addEventListener('click', function (event) { onBarClick(root, event); });

    var galleryWrapper = root.querySelector('[data-kk-pdp-gallery]');
    if (galleryWrapper) {
      // Capture phase: runs before Prestige's own click handler on the carousel
      galleryWrapper.addEventListener('click', function (event) { onGalleryClick(root, event); }, true);
      // Hook PhotoSwipe before Prestige opens it (its listener sits on <product-gallery> itself)
      galleryWrapper.addEventListener('lightbox:open', function (event) {
        var gallery = event.target.closest('product-gallery') || galleryWrapper.querySelector('product-gallery');
        if (gallery) hookLightbox(root, gallery);
      }, true);

      // The hint sits on the slider itself (above the dots), so move it next to the carousel
      var hint = root.querySelector('[data-kk-pdp-tap-hint]');
      var imageList = galleryWrapper.querySelector('.product-gallery__image-list');
      if (hint && imageList) imageList.appendChild(hint);
      if (hint && !storageGet(HINT_KEY)) hint.hidden = false;
    }

    var scheduled = false;
    var schedule = function () {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(function () { scheduled = false; refresh(root); });
    };

    // Column heights change on image load, accordion toggle, variant change...
    if ('ResizeObserver' in window) {
      root.kkResizeObserver = new ResizeObserver(function () { updatePdpSticky(root); });
      root.querySelectorAll('[data-pdp-sticky]').forEach(function (col) { root.kkResizeObserver.observe(col); });
    }
    // ...and Prestige swaps the price / buttons (or the whole content) on variant change
    root.kkMutationObserver = new MutationObserver(function (mutations) {
      var relevant = mutations.some(function (m) { return !m.target.closest || !m.target.closest('.pswp'); });
      if (relevant) schedule();
    });
    root.kkMutationObserver.observe(root, { childList: true, subtree: true });

    root.kkOnResize = function () { updatePdpSticky(root); updateBarSpace(root); };
    window.addEventListener('resize', root.kkOnResize);
    window.addEventListener('load', root.kkOnResize, { once: true });

    loadSimilar(root);
    refresh(root);
  }

  function destroy(root) {
    if (root.kkResizeObserver) root.kkResizeObserver.disconnect();
    if (root.kkMutationObserver) root.kkMutationObserver.disconnect();
    if (root.kkBarObserver) root.kkBarObserver.disconnect();
    if (root.kkOnResize) window.removeEventListener('resize', root.kkOnResize);
    if (root.kkOnDocClick) document.removeEventListener('click', root.kkOnDocClick);
    document.documentElement.classList.remove('kk-pdp-has-bar', 'kk-pdp-zoom-open');
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-kk-pdp]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) { initAll(event.target); });
  document.addEventListener('shopify:section:unload', function (event) {
    event.target.querySelectorAll('[data-kk-pdp]').forEach(destroy);
  });
})();
